import { Injectable } from '@angular/core'
import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { StringService } from '@lemon/core/service/string.service'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of as observableOf, ReplaySubject } from 'rxjs'
import { map, shareReplay, switchMap, take } from 'rxjs/operators'
import { FirebaseLemonaid } from '../firebase-lemonaid.service'
import { KayttajaService } from '../kayttaja.service'
import { TimestampService } from '../../../_jaettu-angular/service/timestamp-service'

export interface LaskutusprofiiliVaihtoehto {
  avain: string
  nimi: string
  oletusprofiili: boolean
}

interface Laskutusprofiilit {
  oletusprofiiliAvain: string | null
  laskutusprofiilit: Laskuasetukset[]
}

@Injectable()
export class LaskutusprofiiliService {
  private _laskutusprofiilitSubject = new ReplaySubject<Laskutusprofiilit>(1)
  private _valittuLaskutusprofiiliAvainSubject = new BehaviorSubject<string | null>(null)

  laskutusprofiiliVaihtoehdotObservable: Observable<LaskutusprofiiliVaihtoehto[]>
  laskutusprofiilienIbanitObservable: Observable<string[]>
  valittuLaskutusprofiiliObservable: Observable<Laskuasetukset | null>

  constructor(
    private _kayttajaService: KayttajaService,
    private _lemonaidFirebase: FirebaseLemonaid,
    private _laskuUriService: LaskuUriService,
    private _laskuKopioija: LaskuKopioija,
    private _timestampService: TimestampService,
    private _stringService: StringService,
  ) {
    this._kayttajaService.kayttajanTiedotObservable
      .pipe(
        switchMap(kayttajanTiedot => {
          if (kayttajanTiedot) {
            const laskutusprofiilitUri = this._laskuUriService.getLaskuasetuksetCollectionUri(kayttajanTiedot.asiakasAvain)
            return this._lemonaidFirebase
              .firestoreCollection<Laskuasetukset>(laskutusprofiilitUri)
              .listenSnapshots()
              .pipe(
                map(snapshots => {
                  const laskutusprofiilit = snapshots.map(snapshot => ({ ...snapshot.data(), avain: snapshot.id }))
                  const jarjestetytLaskutusprofiilit: Laskutusprofiilit = {
                    oletusprofiiliAvain: kayttajanTiedot.asiakasAvain,
                    laskutusprofiilit: this._jarjestaLaskutusprofiilit(laskutusprofiilit, kayttajanTiedot.asiakasAvain),
                  }
                  return jarjestetytLaskutusprofiilit
                }),
              )
          }
          return observableOf<Laskutusprofiilit>({ oletusprofiiliAvain: null, laskutusprofiilit: [] })
        }),
      )
      .subscribe(laskutusprofiilit => {
        this._laskutusprofiilitSubject.next(laskutusprofiilit)
      })

    this.laskutusprofiiliVaihtoehdotObservable = this._laskutusprofiilitSubject.pipe(
      map(({ oletusprofiiliAvain, laskutusprofiilit }) =>
        laskutusprofiilit.map(laskutusprofiili => ({
          avain: laskutusprofiili.avain,
          nimi: this.annaLaskutusprofiilinNimi(laskutusprofiili),
          oletusprofiili: laskutusprofiili.avain === oletusprofiiliAvain,
        })),
      ),
    )

    this.laskutusprofiilienIbanitObservable = this._laskutusprofiilitSubject.pipe(
      map(({ laskutusprofiilit }) =>
        laskutusprofiilit
          .map(laskutusprofiili => laskutusprofiili.iban)
          .filter(iban => !!iban)
          .map(iban => this._stringService.removeAllWhiteSpaces(iban)),
      ),
    )

    this.valittuLaskutusprofiiliObservable = combineLatest([this._kayttajaService.kayttajanTiedotObservable, this._valittuLaskutusprofiiliAvainSubject]).pipe(
      switchMap(([kayttajanTiedot, valittuAvain]) => {
        if (!kayttajanTiedot) {
          return observableOf<Laskuasetukset>(null)
        }
        const laskutusprofiiliAvain = valittuAvain ?? kayttajanTiedot.asiakasAvain
        return this._laskutusprofiilinTilaObservable(kayttajanTiedot.asiakasAvain, laskutusprofiiliAvain).pipe(
          switchMap(laskutusprofiili => {
            const kaytetaankoOletusprofiilia = !laskutusprofiili && laskutusprofiiliAvain !== kayttajanTiedot.asiakasAvain
            if (kaytetaankoOletusprofiilia) {
              return this._laskutusprofiilinTilaObservable(kayttajanTiedot.asiakasAvain, kayttajanTiedot.asiakasAvain)
            }
            return observableOf(laskutusprofiili)
          }),
        )
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  annaLaskutusprofiiliObservable(laskutusprofiiliAvain: string | undefined): Observable<Laskuasetukset | null> {
    return this._laskutusprofiilitSubject.pipe(
      take(1),
      map(laskutusprofiilit => this._etsiLaskutusprofiili(laskutusprofiilit, laskutusprofiiliAvain)),
    )
  }

  annaLaskutusprofiili(laskutusprofiiliAvain: string | undefined): Promise<Laskuasetukset | null> {
    return firstValueFrom(this.annaLaskutusprofiiliObservable(laskutusprofiiliAvain))
  }

  valitseLaskutusprofiili(laskutusprofiiliAvain: string | null) {
    this._valittuLaskutusprofiiliAvainSubject.next(laskutusprofiiliAvain)
  }

  async luoLaskutusprofiili(lahdeprofiili: Laskuasetukset, laskutusprofiilinNimi: string): Promise<string> {
    const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
    const uusiLaskutusprofiili = this._laskuKopioija.copyAsetukset(lahdeprofiili)
    uusiLaskutusprofiili.avain = this._lemonaidFirebase.firestoreCreateId()
    uusiLaskutusprofiili.laskutusprofiilinNimi = laskutusprofiilinNimi
    uusiLaskutusprofiili.date = this._timestampService.now()
    const uri = this._laskuUriService.getLaskuasetuksetUri(kayttajanTiedot.asiakasAvain, uusiLaskutusprofiili.avain)
    await this._lemonaidFirebase.firestoreSetData<Laskuasetukset>(uri, uusiLaskutusprofiili)
    return uusiLaskutusprofiili.avain
  }

  async poistaLaskutusprofiili(laskutusprofiiliAvain: string) {
    const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
    const uri = this._laskuUriService.getLaskuasetuksetUri(kayttajanTiedot.asiakasAvain, laskutusprofiiliAvain)
    await this._lemonaidFirebase.firestoreDeleteDoc(uri)
  }

  annaLaskutusprofiilinNimi(laskutusprofiili: Laskuasetukset): string {
    return laskutusprofiili.laskutusprofiilinNimi || laskutusprofiili.nimi
  }

  private _laskutusprofiilinTilaObservable(asiakasAvain: string, laskutusprofiiliAvain: string): Observable<Laskuasetukset | null> {
    const laskutusprofiiliUri = this._laskuUriService.getLaskuasetuksetUri(asiakasAvain, laskutusprofiiliAvain)
    return this._lemonaidFirebase
      .firestoreDoc<Laskuasetukset>(laskutusprofiiliUri)
      .listen()
      .pipe(map(laskutusprofiili => (laskutusprofiili ? { ...laskutusprofiili, avain: laskutusprofiiliAvain } : null)))
  }

  private _etsiLaskutusprofiili({ oletusprofiiliAvain, laskutusprofiilit }: Laskutusprofiilit, laskutusprofiiliAvain: string | null | undefined): Laskuasetukset | null {
    const haettuProfiili = laskutusprofiilit.find(laskutusprofiili => laskutusprofiili.avain === laskutusprofiiliAvain)
    const oletusprofiili = laskutusprofiilit.find(laskutusprofiili => laskutusprofiili.avain === oletusprofiiliAvain)
    return haettuProfiili ?? oletusprofiili ?? null
  }

  private _jarjestaLaskutusprofiilit(laskutusprofiilit: Laskuasetukset[], asiakasAvain: string): Laskuasetukset[] {
    const oletusprofiili = laskutusprofiilit.find(laskutusprofiili => laskutusprofiili.avain === asiakasAvain)
    const muutProfiilit = laskutusprofiilit
      .filter(laskutusprofiili => laskutusprofiili.avain !== asiakasAvain)
      .sort((a, b) => this.annaLaskutusprofiilinNimi(a).localeCompare(this.annaLaskutusprofiilinNimi(b)))
    return oletusprofiili ? [oletusprofiili, ...muutProfiilit] : muutProfiilit
  }
}
